import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import FolderSelector from 'file-browser/FolderSelector';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK, GRAY_DARKER, GRAY_LIGHT, RED} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import Modal from 'ui-library/modals/Modal';

import {getModelDisplayName, getModelIconUrl, MAX_BULK_PREDICTION_ITEMS} from '../ScientificModelConstants';

interface BulkPredictionModalProps {
  model: string;
  inputTypes: Array<string>;
  onRequestClose: () => void;
  onRequestSubmit: (success: boolean) => void;
}

// TODO: inputTypes and onRequestSubmit are used in the next PR to wire up item management and submission.
export default function BulkPredictionModal({model, onRequestClose}: BulkPredictionModalProps): JSX.Element {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [error] = useState<string | null>(null);

  const displayName = getModelDisplayName(model);
  const iconUrl = getModelIconUrl(model);
  const itemCount = 0;

  const canSubmit = selectedFolderId != null && itemCount > 0;

  const getDisabledReason = (): string | null => {
    if (selectedFolderId == null) {
      return 'Please select a folder';
    }
    if (itemCount === 0) {
      return 'Please add at least one item';
    }
    return null;
  };

  const disabledReason = getDisabledReason();

  return (
    <Modal
      title={
        <div className={css(styles.titleRow)}>
          <img src={iconUrl} alt={displayName} className={css(styles.titleIcon)} />
          <span>Configure {displayName} Run</span>
        </div>
      }
      onRequestClose={onRequestClose}
      large={true}
      fullHeight={true}
      bodyPadded={false}
      scrollable={false}
    >
      <div className={css(styles.outerContainer)}>
        <div className={css(styles.content)}>
          {error && (
            <div className={css(styles.errorBanner)}>
              <InlineBanner text={error} bannerType={InlineBannerType.ERROR} />
            </div>
          )}

          {/* Folder Picker */}
          <div className={css(styles.section)}>
            <div className={css(styles.label)}>
              Output Folder <span className={css(styles.required)}>*</span>
            </div>
            <div className={css(styles.folderSelectorContainer)}>
              <FolderSelector
                value={selectedFolderId}
                onChange={(e: {target: {value: string | null}}) => {
                  setSelectedFolderId(e.target.value);
                }}
                allowNone={false}
                enableRedesign={false}
                error=""
                level="append"
                noFolderText="Choose folder"
                readOnly={false}
                requireSubfolderCreatableAuth={false}
                showInventory={false}
                showNotebook={true}
                showRegistries={false}
                unsetOnFetchFail={false}
                checkExistingItemForDataModsMovement={false}
              />
            </div>
          </div>

          {/* Items list placeholder — full functionality added in next PR */}
          <div className={css(styles.section, styles.itemsSection)}>
            <div className={css(styles.label)}>Predictions</div>
            <div className={css(styles.tableWrapper)}>
              <div className={css(styles.headerRow)}>
                <span>Input Entity</span>
                <span className={css(styles.countIndicator)}>
                  {itemCount}/{MAX_BULK_PREDICTION_ITEMS}
                </span>
              </div>
              <div className={css(styles.placeholderCell)}>
                No items added yet. Use the options above to add items.
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={css(styles.footer)}>
          <Tooltip text={disabledReason} isDisabled={disabledReason == null}>
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.MEDIUM}
              text="Create Run"
              disabled={!canSubmit}
              onClick={() => {}}
            />
          </Tooltip>
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '20px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  titleIcon: {
    width: 24,
    height: 24,
    objectFit: 'contain',
  },
  section: {
    marginBottom: '20px',
  },
  itemsSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  label: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    fontWeight: 600,
    marginBottom: '8px',
  },
  required: {
    color: RED,
  },
  folderSelectorContainer: {
    maxWidth: '350px',
  },
  errorBanner: {
    marginBottom: '16px',
  },
  tableWrapper: {
    flex: 1,
    overflowY: 'auto',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
  },
  headerRow: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    fontWeight: 600,
    padding: '8px 12px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  countIndicator: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARK,
    fontWeight: 400,
  },
  placeholderCell: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARK,
    padding: '32px 16px',
    textAlign: 'center',
  },
  footer: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    padding: '12px 20px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
});
